import ChatLogoCvna from '@carvana/ds-icons/ChatLogoCvna';
import { icon } from '@carvana/ds-styles';
import DispatcherEvent from '@carvana/merch-native/DispatcherEvent';
import { datadogRum } from '@datadog/browser-rum';
import cx from 'classnames';
import { useCallback } from 'react';
import { SectionViewedLogger } from '@/vdp/Analytics';
import Section, { SectionName } from '~/constants/vdp/section';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  SebastianQuestion,
  useSebastianStatus
} from '~/context/common/SebastianContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import useIsInInspectionExperiment from '~/hooks/vdp/useIsInInspectionExperiment';
import useSebastianQuestions from '~/hooks/vdp/useSebastianQuestions';
import { sendMessageToNative } from '~/native/vdp/dispatchers';
import { generateErrorLogger } from '~/utilities/loggers';
import Questions from './Questions';
import { Container } from './SebastianQuestions.styles';

const SECTION_HEADINGS = {
  [Section.OwnerReviews]: 'Interested in what customers are saying?',
  [Section.PriceDetails]: 'Need some help?',
  [Section.WhyBuy]: 'Looking for specific vehicle info?'
};

type OwnProps = {
  className?: string;
  section: Section.WhyBuy | Section.OwnerReviews | Section.PriceDetails;
};

export default function SebastianQuestions({ className, section }: OwnProps) {
  const sebastianStatus = useSebastianStatus();
  const eventCorrelationId = useEventCorrelationId();
  const trackEvent = useTrackEvent();
  const isInInspectionExperiment = useIsInInspectionExperiment();
  const { isLoaded, questions, showSection } = useSebastianQuestions(section);

  const handleQuestionClick = useCallback(
    async (question?: SebastianQuestion) => {
      if (question) {
        window.CarvanaChatUi?.initializeAndSendQuestion?.({
          question: question.question,
          question_id: question.id,
          question_source: 'vdp-sebs-expansion',
          view_correlation_id: eventCorrelationId
        }).catch(error => {
          generateErrorLogger('SebastianQuestions.initializeAndSendQuestion')(
            error
          );
        });
      } else {
        window.CarvanaChatUi?.show?.();
      }

      sendMessageToNative(DispatcherEvent.SebastianPrompt, {
        correlationId: eventCorrelationId,
        ...(question && {
          prompt: {
            question: question.question,
            id: question.id
          }
        })
      });

      trackEvent(SectionName.SebastianQuestions, {
        action: 'clicked',
        questionId: question?.id ?? null,
        text: question?.question ?? null,
        section: section
      });

      datadogRum.addAction('sebastian-question-clicked', {
        questionId: question?.id ?? null,
        question: question?.question ?? null,
        section: section
      });
    },
    [eventCorrelationId, section, trackEvent]
  );

  if (!showSection) return null;

  return (
    <SectionViewedLogger
      additionalCAs={{ section }}
      section={Section.SebastianQuestions}
    >
      <Container
        className={cx(className, {
          'bg-background-weak':
            isInInspectionExperiment && section === Section.OwnerReviews
        })}
        data-testid={`sebastian-questions-${section}`}
      >
        <div className="flex flex-row items-center gap-md">
          <div className="flex h-full w-24 self-start md:items-center md:self-center">
            <ChatLogoCvna
              size={24}
              color={icon.strongActive}
              variant="sparkle"
            />
          </div>

          <h2 className="text-text-strong t-heading-sm md:t-heading-md">
            {SECTION_HEADINGS[section]}
          </h2>
        </div>

        <Questions
          handleQuestionClick={handleQuestionClick}
          questions={questions}
          isLoaded={isLoaded}
          sebastianStatus={sebastianStatus}
        />
      </Container>
    </SectionViewedLogger>
  );
}
